import { Injectable } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { Context } from "../context";
import { BrandModel } from "../models/brand.model";
import { logError } from "../utils/log-error";
import { ApiService } from "./api.service";
import { SettingsService } from "./settings.service";
import { ThemeService } from "./theme.service";

@Injectable({
  providedIn: 'root'
})
export class BrandService {

  constructor(
    private api: ApiService,
    private settings: SettingsService,
    private title: Title,
    private theme: ThemeService
  ) { }

  async init() {
    try {
      await this.refreshBrand();
    } catch (error) {
      const cachedBrand = await this.settings.brand();
      if (!cachedBrand)
        throw error;

      // An installed PWA can still start offline using its last known brand.
      this.apply(cachedBrand);
      logError('Unable to refresh brand configuration; using the cached configuration.', error);
    }
  }

  private async refreshBrand() {
    const configuration = await this.api.getAppConfiguration();
    const brand = configuration.brand;

    this.apply(brand);

    // A successful server response is authoritative. Persist it only after
    // applying it so storage failures cannot make a fresh response fall back
    // to an older cached configuration.
    try {
      await this.settings.brand(brand);
    } catch (error) {
      logError('Unable to cache brand configuration.', error);
    }
  }

  private apply(brand: BrandModel) {
    // Preserve the object reference used by already-created components while
    // replacing the complete nested appearance configuration.
    if (Context.brand)
      Object.assign(Context.brand, brand);
    else
      Context.brand = brand;

    this.title.setTitle(brand.title);
    this.theme.apply(brand);
  }
}
